"use client";

import { useState, useEffect, useContext } from "react";
import { FiChevronRight, FiCheck } from "react-icons/fi";
import { usePathname } from "next/navigation";

import { Button } from "@/components/Button";

import { QREditorContext } from "@/contexts/QREditorContext";

import { getPageIndexFromUrl } from "@/utils/qrEditorNavigatorHelper";

import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { StepButtonItemProps, StepperProps } from "./types";

import "./styles.scss";

const formatButtons = (
  qrEditorContext: QREditorContextProps<any>,
  isValidForm: boolean,
  stepButtons: StepButtonItemProps[],
  pageIndex: number
) => {
  const stepperWithStyle = [];

  const getButtonStyles = (index: number) => {
    const styles = [];
    if (index < pageIndex) styles.push("step--done");
    if (isValidForm && Math.abs(index - pageIndex) === 1) styles.push("disabled--valid");

    return styles.join(" ");
  };

  stepButtons.forEach((button, index) => {
    stepperWithStyle.push(
      <div
        className="step"
        key={`button-${index}`}>
        <Button
          dataQA={button.dataQA}
          label={button.label}
          step={index < pageIndex ? <FiCheck style={{ color: "#00af63" }} /> : index + 1}
          disabled={index > qrEditorContext?.qrData.currentPageIndex}
          className={getButtonStyles(index)}
        />
        {index < stepButtons.length - 1 && (
          <FiChevronRight
            key={`arrow-${index}`}
            className="Stepper__steps__divider"
          />
        )}
      </div>
    );
  });

  return stepperWithStyle;
};

export const Stepper = (props: StepperProps) => {
  const pathname = props.location ? props.location : usePathname();

  const { stepButtons, isValidForm } = props;

  const qrEditorContext = useContext(QREditorContext);

  const currentPageIndex = getPageIndexFromUrl(pathname);
  const [stepperButtons, setStepperButtons] = useState<StepButtonItemProps[]>(
    formatButtons(qrEditorContext, isValidForm, stepButtons, currentPageIndex)
  );

  useEffect(() => {
    const stepperWithStyle = formatButtons(
      qrEditorContext,
      isValidForm,
      stepButtons,
      currentPageIndex
    );

    setStepperButtons(stepperWithStyle);
  }, [stepButtons, pathname]);

  return (
    <div
      data-testid="Stepper"
      className="Stepper">
      <div
        className="Stepper__steps"
        data-testid="Stepper__steps">
        {stepperButtons as any}
      </div>
    </div>
  );
};
